import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, FormikErrors, FormikHelpers } from "formik";
import {
    Button,
    Checkbox,
    Typography,
    FormHelperText,
    FormControlLabel,
    Tooltip,
    FormGroup,
    Alert,
    FormControl,
    FormLabel,
    AccordionDetails,
    AccordionSummary,
    Accordion,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { shiftDeletionFormSchema } from "validations/schemas";
import { useStyles } from "./ShiftDeletionForm.styles";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import {
    IShiftDeletionForm,
    IShiftDeletionFormValues,
    IShiftsAssignmentWithDetailsFormat,
} from "./ShiftDeletionFormInterfaces";
import { checkShiftOverlapping } from "utilities/utilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";
import { translateDefaultEntity } from "utilities/localesUtilities";
import moment from "moment";
import useDateFormat from "hooks/useDateFormat";
import { IShift } from "../../../MillSettingsStepInterfaces";
import { shiftChangesFormatter } from "./utils";

export const ShiftDeletionForm = ({
    shiftDeletionInfo,
    handleClose,
    setFieldValueMillSettingsForm,
    valuesMillSettings,
}: IShiftDeletionForm) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { formatDate } = useDateFormat();

    const [overlappingShifts, setOverlappingShifts] = useState<boolean>(false);
    const [expand, setExpand] = useState<number>(0);

    const effectiveDate = moment.utc().add(1, "day").startOf("day").toDate();
    const shiftsOptions = useMemo(
        () => (shiftDeletionInfo?.deletionData ? [...shiftDeletionInfo?.deletionData.shiftsOptions] : []),
        [shiftDeletionInfo],
    );

    const handleSubmit = useCallback(
        async (values: IShiftDeletionFormValues, { setSubmitting }: FormikHelpers<IShiftDeletionFormValues>) => {
            if (overlappingShifts) {
                setSubmitting(false);
                return;
            }

            if (shiftDeletionInfo) {
                const deletedShift = { ...valuesMillSettings.shifts[shiftDeletionInfo.index] };
                deletedShift.disabled_at = effectiveDate;
                if (shiftDeletionInfo.shift.shift_id) {
                    deletedShift.production_line_shifts_changes = shiftDeletionInfo.shift.production_lines.map(
                        (pl) => ({
                            production_line_id: pl.production_line_id,
                            production_line_shift_id: pl.production_line_shift_id,
                            disabled_at: effectiveDate,
                            name: pl.name,
                        }),
                    );
                }
                const changedShifts = [
                    ...valuesMillSettings.shifts.slice(0, shiftDeletionInfo.index),
                    deletedShift,
                    ...valuesMillSettings.shifts.slice(shiftDeletionInfo.index + 1),
                ];
                const allShiftsSoftDeleted =
                    changedShifts.filter((changedShift, indexFilter) =>
                        changedShift.shift_id
                            ? !changedShift.deleted_at || !changedShift.disabled_at
                            : indexFilter !== shiftDeletionInfo.index,
                    ).length === 0;

                if (allShiftsSoftDeleted) setFieldValueMillSettingsForm("allShiftsSoftDeleted", true);

                const productionLinesShiftAssignmentsDetails: IShiftsAssignmentWithDetailsFormat[] =
                    values.productionLineShiftIds.map((plShiftIds) => {
                        return {
                            production_line_id: plShiftIds.productionLine.id,
                            name: plShiftIds.productionLine.name,
                            shiftsIdsDetails: plShiftIds.shiftIds.map((id) => {
                                const shiftDetail = shiftsOptions.find((option) => option.shift_id === id);
                                return {
                                    id,
                                    name: shiftDetail?.name,
                                };
                            }),
                        };
                    });

                let changedShiftsProductionLinesAssigned: IShift[] = [];
                if (shiftDeletionInfo.shift?.shift_id) {
                    changedShiftsProductionLinesAssigned = shiftChangesFormatter(
                        changedShifts,
                        productionLinesShiftAssignmentsDetails,
                        effectiveDate,
                    );

                    setFieldValueMillSettingsForm("shifts", changedShiftsProductionLinesAssigned);
                } else {
                    changedShiftsProductionLinesAssigned = shiftChangesFormatter(
                        valuesMillSettings.shifts,
                        productionLinesShiftAssignmentsDetails,
                        effectiveDate,
                    );

                    setFieldValueMillSettingsForm("shifts", changedShiftsProductionLinesAssigned);
                }
            }
            handleClose();
        },
        [
            overlappingShifts,
            setFieldValueMillSettingsForm,
            shiftDeletionInfo,
            shiftsOptions,
            valuesMillSettings,
            handleClose,
            effectiveDate,
        ],
    );

    const handleShiftsOverlap = useCallback(
        (ids: number[]) => {
            shiftsOptions.map((option) => ({
                ...option,
                shift_historic: [option.shift_historic.days[0]],
            }));
            const isProductionLineCreation = false;
            const overlap = checkShiftOverlapping(ids, shiftsOptions, isProductionLineCreation);

            setOverlappingShifts(overlap);
        },
        [shiftsOptions],
    );

    const initFormValues: IShiftDeletionFormValues = useMemo(() => {
        return {
            productionLineShiftIds:
                shiftDeletionInfo?.deletionData?.productionLinesOptions.map((plOption) => ({
                    productionLine: { ...plOption },
                    shiftIds: [],
                })) || [],
        };
    }, [shiftDeletionInfo]);

    return (
        <Formik initialValues={initFormValues} validationSchema={shiftDeletionFormSchema} onSubmit={handleSubmit}>
            {({ isSubmitting, errors, touched, values, setFieldValue }) => {
                return (
                    <Form className={classes.formContainer}>
                        <div className={classes.modalBody}>
                            <Typography className={classes.instruction}>
                                {t("mill_setup.shifts.deletion_shift_message")}
                            </Typography>
                            <div className={classes.modalAccordionContainer}>
                                {values.productionLineShiftIds.map((productionLine, index) => {
                                    return (
                                        <Accordion
                                            key={productionLine.productionLine.id}
                                            onChange={() => {
                                                setExpand(index);
                                            }}
                                            className={classes.card}
                                            expanded={index === expand}
                                            sx={{ width: "100%" }}
                                        >
                                            <AccordionSummary className={classes.cardHeader}>
                                                <KeyboardArrowDownIcon
                                                    fontSize="small"
                                                    className={classes.cardHeaderIcon}
                                                    style={{
                                                        transform: index === expand ? "rotate(180deg)" : "rotate(0deg)",
                                                    }}
                                                />
                                                <div className={classes.cardTitle}>
                                                    {productionLine.productionLine.name}
                                                </div>
                                                {!productionLine.shiftIds.length ? (
                                                    <Alert className={classes.cardAlert} severity="warning">
                                                        {t("mill_setup.shifts.deletion_modal_warning")}
                                                    </Alert>
                                                ) : null}
                                            </AccordionSummary>
                                            <AccordionDetails className={classes.cardBody}>
                                                {index === expand ? (
                                                    <div className={classes.cardBodyContent}>
                                                        {" "}
                                                        {
                                                            <FormControl
                                                                error={
                                                                    overlappingShifts
                                                                        ? overlappingShifts
                                                                        : touched.productionLineShiftIds &&
                                                                          Boolean(errors.productionLineShiftIds)
                                                                }
                                                                className={classes.boxContainer}
                                                                component="fieldset"
                                                                variant="standard"
                                                            >
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        gap: "5px",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    <FormLabel className={classes.formLabel}>
                                                                        {t(
                                                                            "corporate_backoffice.production_line_view.production_line_creation_form.shifts",
                                                                        )}
                                                                    </FormLabel>

                                                                    <Alert
                                                                        sx={{
                                                                            alignSelf: "center",
                                                                            padding: "5px 10px",
                                                                        }}
                                                                        severity="info"
                                                                    >
                                                                        {t(
                                                                            "corporate_backoffice.production_line_view.production_line_creation_form.warnings.shift_change.message",
                                                                            {
                                                                                date: formatDate(effectiveDate),
                                                                            },
                                                                        )}
                                                                    </Alert>
                                                                </div>
                                                                <FormGroup
                                                                    style={{
                                                                        display: "flex",
                                                                        flexDirection: "row",
                                                                        width: "100%",
                                                                    }}
                                                                >
                                                                    {shiftsOptions.map((shift) => {
                                                                        return (
                                                                            <Tooltip
                                                                                key={shift.shift_id}
                                                                                PopperProps={{
                                                                                    className: classes.tooltip,
                                                                                }}
                                                                                title={
                                                                                    <>
                                                                                        <Typography
                                                                                            className={
                                                                                                classes.tooltipTexts
                                                                                            }
                                                                                        >
                                                                                            {t(
                                                                                                "corporate_backoffice.production_line_view.production_line_creation_form.start",
                                                                                            )}
                                                                                            :{" "}
                                                                                            {shift.shift_historic.start}
                                                                                        </Typography>
                                                                                        <Typography
                                                                                            className={
                                                                                                classes.tooltipTexts
                                                                                            }
                                                                                        >
                                                                                            {t(
                                                                                                "corporate_backoffice.production_line_view.production_line_creation_form.end",
                                                                                            )}
                                                                                            : {shift.shift_historic.end}
                                                                                        </Typography>
                                                                                        <Typography
                                                                                            className={
                                                                                                classes.tooltipTexts
                                                                                            }
                                                                                        >
                                                                                            {t(
                                                                                                "corporate_backoffice.production_line_view.production_line_creation_form.days",
                                                                                            )}
                                                                                            :{" "}
                                                                                            {shift.shift_historic
                                                                                                ?.daysDetails &&
                                                                                                shift.shift_historic.daysDetails.map(
                                                                                                    (day) =>
                                                                                                        `${translateDefaultEntity(
                                                                                                            i18n.language,
                                                                                                            day,
                                                                                                            "name",
                                                                                                            DefaultTranslationFieldsInTable.DAY,
                                                                                                        )} `,
                                                                                                )}
                                                                                        </Typography>
                                                                                    </>
                                                                                }
                                                                            >
                                                                                <FormControlLabel
                                                                                    control={
                                                                                        <Checkbox
                                                                                            checked={productionLine.shiftIds.some(
                                                                                                (id) =>
                                                                                                    id ===
                                                                                                    shift.shift_id,
                                                                                            )}
                                                                                            value={shift.shift_id}
                                                                                            onChange={(e) => {
                                                                                                let deepClonedShiftsIds: number[] =
                                                                                                    JSON.parse(
                                                                                                        JSON.stringify(
                                                                                                            productionLine.shiftIds,
                                                                                                        ),
                                                                                                    );
                                                                                                let editedProductionLineShiftIds =
                                                                                                    [
                                                                                                        ...values.productionLineShiftIds,
                                                                                                    ];
                                                                                                if (e.target.checked) {
                                                                                                    const shiftsIds = [
                                                                                                        ...deepClonedShiftsIds,
                                                                                                        Number(
                                                                                                            e.target
                                                                                                                .value,
                                                                                                        ),
                                                                                                    ];
                                                                                                    editedProductionLineShiftIds[
                                                                                                        index
                                                                                                    ] = {
                                                                                                        ...editedProductionLineShiftIds[
                                                                                                            index
                                                                                                        ],
                                                                                                        shiftIds:
                                                                                                            shiftsIds,
                                                                                                    };
                                                                                                    setFieldValue(
                                                                                                        "productionLineShiftIds",
                                                                                                        editedProductionLineShiftIds,
                                                                                                    );
                                                                                                    handleShiftsOverlap(
                                                                                                        shiftsIds,
                                                                                                    );
                                                                                                } else {
                                                                                                    const shiftsIds =
                                                                                                        deepClonedShiftsIds.filter(
                                                                                                            (shiftId) =>
                                                                                                                Number(
                                                                                                                    e
                                                                                                                        .target
                                                                                                                        .value,
                                                                                                                ) !==
                                                                                                                shiftId,
                                                                                                        );
                                                                                                    editedProductionLineShiftIds[
                                                                                                        index
                                                                                                    ] = {
                                                                                                        ...editedProductionLineShiftIds[
                                                                                                            index
                                                                                                        ],
                                                                                                        shiftIds:
                                                                                                            shiftsIds,
                                                                                                    };
                                                                                                    setFieldValue(
                                                                                                        "productionLineShiftIds",
                                                                                                        editedProductionLineShiftIds,
                                                                                                    );
                                                                                                    handleShiftsOverlap(
                                                                                                        shiftsIds,
                                                                                                    );
                                                                                                }
                                                                                            }}
                                                                                            className={classes.checkbox}
                                                                                        />
                                                                                    }
                                                                                    label={shift.name}
                                                                                />
                                                                            </Tooltip>
                                                                        );
                                                                    })}
                                                                </FormGroup>
                                                                {overlappingShifts && (
                                                                    <FormHelperText>
                                                                        {t(
                                                                            "corporate_backoffice.production_line_view.production_line_creation_form.errors.overlapping_shifts",
                                                                        )}
                                                                    </FormHelperText>
                                                                )}
                                                                <FormHelperText>
                                                                    {touched.productionLineShiftIds?.[index] &&
                                                                        (
                                                                            errors.productionLineShiftIds as FormikErrors<
                                                                                IShiftDeletionFormValues["productionLineShiftIds"]
                                                                            >
                                                                        )?.[index]?.shiftIds &&
                                                                        t("field_errors.at_least_one_item_required")}
                                                                </FormHelperText>
                                                            </FormControl>
                                                        }{" "}
                                                    </div>
                                                ) : null}
                                            </AccordionDetails>
                                        </Accordion>
                                    );
                                })}
                            </div>
                        </div>
                        <div className={classes.footer}>
                            <Button
                                variant="text"
                                color="info"
                                className={classes.button}
                                onClick={() => {
                                    handleClose();
                                }}
                                disabled={isSubmitting}
                            >
                                {t("corporate_backoffice.cluster_view.cluster_creation_form.cancel")}
                            </Button>
                            <Button
                                variant="contained"
                                color="info"
                                className={classes.submitButton}
                                type="submit"
                                disabled={isSubmitting}
                            >
                                <CheckOutlinedIcon />
                                {t("corporate_backoffice.cluster_view.cluster_creation_form.confirm")}
                            </Button>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};
